import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoredLanguage, setStoredLanguage } from '@/utils/language';

type LanguageContextType = {
  t: (key: string, options?: Record<string, string | number | null>) => string;
  i18n: {
    language: string;
    changeLanguage: (lng: string) => void;
  };
  changeLanguage: (lng: string) => void;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode; }) => {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng: string) => {
    setStoredLanguage(lng as 'en' | 'es');
    i18n.changeLanguage(lng);
  };

  // Initialize from stored language preference (falls back to navigator inside getStoredLanguage)
  useEffect(() => {
    const lang = getStoredLanguage();
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [i18n]);

  return (
    <LanguageContext.Provider value={{ t, i18n, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
};
